import {
  format,
  subDays,
  startOfDay,
  startOfMonth,
  endOfMonth,
  subMonths,
  differenceInDays,
  isValid,
  parseISO,
} from "date-fns";
import type { DateRangeOption } from "@/components/clicks/DateRangeFilter";

export interface DateRangeResult {
  start: string | null;
  end: string;
}

export interface CompareDateRages {
  current: DateRangeResult;
  previous: DateRangeResult;
}

const formatDate = (date: Date): string => format(date, "yyyy-MM-dd");

export const getDateRangeDates = (
  range: DateRangeOption,
  baseDate: Date = new Date(),
  customStart?: string,
  customEnd?: string
): CompareDateRages => {
  const today = startOfDay(baseDate);

  let currentStart = today;
  let currentEnd = today;
  let prevStart = today;
  let prevEnd = today;

  switch (range) {
    case "today":
      currentStart = today;
      currentEnd = today;
      prevStart = subDays(today, 1);
      prevEnd = subDays(today, 1);
      break;
    case "yesterday":
      currentStart = subDays(today, 1);
      currentEnd = subDays(today, 1);
      prevStart = subDays(today, 2);
      prevEnd = subDays(today, 2);
      break;
    case "last_7_days":
      currentStart = subDays(today, 6);
      currentEnd = today;
      prevStart = subDays(today, 13);
      prevEnd = subDays(today, 7);
      break;
    case "last_14_days":
      currentStart = subDays(today, 13);
      currentEnd = today;
      prevStart = subDays(today, 27);
      prevEnd = subDays(today, 14);
      break;
    case "last_21_days":
      currentStart = subDays(today, 20);
      currentEnd = today;
      prevStart = subDays(today, 41);
      prevEnd = subDays(today, 21);
      break;
    case "last_30_days":
      currentStart = subDays(today, 29);
      currentEnd = today;
      prevStart = subDays(today, 59);
      prevEnd = subDays(today, 30);
      break;
    case "this_month":
      currentStart = startOfMonth(today);
      currentEnd = today;
      prevStart = startOfMonth(subMonths(today, 1));
      prevEnd = endOfMonth(subMonths(today, 1));
      break;
    case "last_month":
      currentStart = startOfMonth(subMonths(today, 1));
      currentEnd = endOfMonth(subMonths(today, 1));
      prevStart = startOfMonth(subMonths(today, 2));
      prevEnd = endOfMonth(subMonths(today, 2));
      break;
    case "all_time":
      return {
        current: { start: null, end: formatDate(today) },
        previous: { start: null, end: formatDate(today) },
      };
    case "custom": {
      if (!customStart || !customEnd) break;

      const startParsed = parseISO(customStart);
      const endParsed = parseISO(customEnd);
      if (!isValid(startParsed) || !isValid(endParsed)) break;

      currentStart = startOfDay(startParsed);
      currentEnd = startOfDay(endParsed);

      const diffDays = differenceInDays(currentEnd, currentStart);

      prevStart = subDays(currentStart, diffDays + 1);
      prevEnd = subDays(currentEnd, diffDays + 1);
      break;
    }
  }

  return {
    current: { start: formatDate(currentStart), end: formatDate(currentEnd) },
    previous: { start: formatDate(prevStart), end: formatDate(prevEnd) },
  };
};